import {useState} from 'react'
import {useNavigate, useParams} from 'react-router'
import {useTripDetails} from '@/hooks/useTrips'
import {tripFlightRecord} from '@/features/fromAirportArrival/tripFlightRecord'
import ArrivalSkeleton from '@/customComponents/fromAirportArrival/ArrivalSkeleton'
import FlightOnTheWay from '@/customComponents/fromAirportArrival/FlightOnTheWay'
import {isFlightArrived} from '@/features/fromAirportArrival/flightStatus'
import PickupDriverCard from '@/customComponents/fromAirportArrival/PickupDriverCard'
import ArrivalNextSteps from '@/customComponents/fromAirportArrival/ArrivalNextSteps'
import TripChat from '@/customComponents/trackDriver/TripChat.jsx'
import {useAnnounceArrival} from '@/features/fromAirportArrival/useAnnounceArrival'
import {arrowRightSm} from '@img/index.js'

/**
 * "WELCOME! YOU'VE ARRIVED." — the screen a passenger meets when their flight lands.
 *
 * ── ⚠️ THE MOBILE DESIGN, WIDENED — NOT A PHONE COLUMN ON A DESKTOP ───────────────
 * The handed-over design is one phone-width stack: next step, walk, driver, car. Rendered
 * as-is in a browser it is a narrow ribbon down the middle of an empty page, which reads
 * as an unfinished screen rather than a deliberate one. So the stack SPLITS at desktop
 * width along the seam the design already has in it — what you do next on the left, who is
 * coming for you on the right — and both halves sit under one shared greeting. Below that
 * width it collapses back to the original single column, in the original order.
 *
 * ── ⚠️ TWO WAYS TO THE SAME BOOTH, OFFERED EQUALLY ────────────────────────────────
 * The live map walk and the written directions are alternatives, not a path and its
 * fallback. Indoor GPS is unreliable enough that the written steps are a first-class
 * route, and a browser leans harder on wifi positioning than a phone does — so the
 * directions matter MORE here than on mobile, not less. Hence two buttons of equal width
 * rather than a button and a link.
 *
 * ── ⚠️ IT DOES NOT ASK FOR LOCATION ITSELF ────────────────────────────────────────
 * It used to, on mount, and the instinct was right — a browser prompts once, and asking
 * while someone is reading beats asking as the map opens. What was wrong was WHO asked: an
 * unattended prompt over a screen nobody had tapped is the one most likely to be dismissed,
 * and a dismissal is a permission spent. `WelcomeModal` asks, from a button. See there.
 */
/**
 * How often the TRIP is re-read while the flight is still in the air.
 *
 * ⚠️ THE TRIP, NOT THE FLIGHT PROVIDER. This used to poll AeroDataBox from the browser
 * once a minute, at a billed request each. The flight now rides on the trip resource —
 * the backend polls the provider and stores the answer — so the same interval costs one
 * call to our own API and, unlike the direct lookup, also picks up a pickup time the
 * backend has shifted underneath the passenger.
 *
 * A minute is the useful floor: it bounds how long a landed passenger stares at a tracking
 * card before the page turns into the welcome screen. The backend itself refreshes every
 * ~15 min inside the watch window, so `last_polled_at` is what says how fresh the answer
 * really is — not this interval.
 */
const TRIP_POLL_MS = 60000

/**
 * ⚠️ IT TAKES ITS `tripId` AS A PROP, because it is no longer a page. `/:tripId/welcome` was
 * deleted (2026-09-23) and `WelcomeModal` is the only thing that renders this — over
 * whatever page the passenger happens to be on, whose URL carries no trip of ours. The
 * `useParams` fallback below is the old route's reading, kept because it costs a line and
 * still resolves correctly on `/:tripId/track-driver`.
 *
 * `embedded` says it is the modal: the greeting block is dropped (the modal header already
 * says it) and every exit closes the modal instead of just navigating.
 */
export default function FromAirportArrived({tripId: tripIdProp = null, embedded = false, onLeave = null}) {
    const {tripId: routeTripId} = useParams()
    const tripId = tripIdProp ?? routeTripId
    const routerNavigate = useNavigate()

    /* ⚠️ EMBEDDED, EVERY EXIT CLOSES THE MODAL ON ITS WAY OUT. The two links below move to
       another page, which as a PAGE was the end of this screen — but as a modal the guide
       would simply go on floating over wherever it sent them, and the passenger would land
       on /track-driver still reading about the walk. `onLeave` is the modal's own close. */
    const navigate = (to) => { routerNavigate(to); if (embedded) onLeave?.() }
    /* ⚠️ `useTripDetails` RESOLVES TO `{trip, paymentMethods}`, NOT THE TRIP. Read as the
       trip itself, every field below is undefined and the screen renders its empty
       state on a perfectly good booking — silently, because nothing throws. */
    /* ⚠️ POLLED, because this screen is reached BEFORE the aircraft is down — see
       `TRIP_POLL_MS`. The flight status is the one thing here that changes by itself, and
       it decides which page this even is. */
    const {data, isLoading: tripLoading} = useTripDetails(tripId, {pollMs: TRIP_POLL_MS})
    const trip = data?.trip || null

    /**
     * THE FLIGHT — the backend's stored provider record, off the trip.
     *
     * ⚠️ NOT A LOOKUP OF OUR OWN ANY MORE. The FE guide is explicit: render flight data
     * from the server, never call the flight provider from a client. `tripFlightRecord`
     * also picks the right LEG of `provider_data` rather than entry [0], which is only
     * correct on a single-leg flight. Everything downstream still sees the provider's own
     * shape, because the backend stores the response body untouched.
     *
     * ⚠️ READ BEFORE THE LOCATION HOOKS, because it is what decides whether they run.
     */
    const flight = tripFlightRecord(trip)

    /**
     * IS THE AIRCRAFT STILL UP? — the one question the whole screen turns on.
     *
     * ⚠️ AN UNKNOWN FLIGHT COUNTS AS DOWN. `flight` is null while `provider_data` has not
     * been filled in, and this page is reached BY the arrival flow. Withholding the walk
     * from someone standing at the gate because we have no record is the worse of the two
     * errors — see the render branch below, which reads the same way.
     */
    const airborne = !!flight && !isFlightArrived(flight)

    /* The backend could not resolve the flight number. Checked before `airborne`: a failed
       lookup has no provider record, so `flight` is null and the page would otherwise take
       it for landed and say "Welcome! You've arrived". */
    const trackingFailed = trip?.flight?.tracking_status === 'failed'

    /**
     * ⚠️ NOT ASKED FOR WHILE THEY ARE IN THE AIR, and this is the whole point of the gate.
     *
     * A browser gives one clean geolocation prompt per origin. Spending it on a passenger
     * at 38,000 feet — nothing to walk to, no driver on the way, and a page that says in
     * so many words that there is nothing to do — is how it comes back DENIED by the time
     * the walk actually needs it. A denial cannot be re-prompted from script.
     *
     * So the watch starts at the moment it becomes useful: the flight is down, the walk is
     * the next thing, and the prompt arrives attached to a screen that explains it.
     */
    /* ⚠️ THIS SCREEN NO LONGER OPENS A WATCH OF ITS OWN (2026-09-22), and the reasoning
       above is the reason why it must not: a browser gives one clean prompt, and spending
       it the instant a screen mounts is how it comes back denied. Mounting the watch WAS
       spending it — the prompt appeared unattended, over whatever the passenger was
       reading, with nothing to press.

       `WelcomeModal` now owns the ask: a card the passenger can see, with a button, raised
       from their tap. The walk screens still open their own watch when entered, which is
       the same principle — a gesture, at the moment it is useful. */

    /**
     * ⚠️ AND SHARED FROM HERE, NOT FROM THE WALK SCREEN ONLY.
     *
     * The driver's pickup screen draws the passenger's pin and works out that they have
     * reached the booth from this stream and nothing else — there is no endpoint that
     * returns a passenger's last position, so every moment with sharing off is a moment the
     * driver spends looking at an empty map.
     *
     * Sharing used to begin only when the passenger opened `WalkToPickup`. But this screen
     * offers TWO routes to the booth, deliberately as equals (see the note at the top): the
     * live map, and the written directions. A passenger who takes the written steps — the
     * likelier choice indoors, where GPS is poor and the design says so — never opens the
     * walk screen at all, and so crossed the entire terminal invisible to the driver who
     * was waiting on exactly that signal.
     *
     * Starting here covers both routes, and covers the passenger who simply reads this
     * screen for a minute before choosing. `useShareMyLocation` throttles to ~1Hz and 5m
     * and holds one request in flight, so the walk screen mounting its own copy afterwards
     * costs nothing.
     */
    /* ⚠️ NO LOCATION SHARING HERE. The portal shares from ONE place — `AppLayout` — on one rule: the first ongoing trip's flight has landed and the ride has not started. A page-level copy would share under its own rule and contradict it. The position is still READ here, for this screen's own map. */

    /* ⚠️ NO RELOAD ON LANDING. It used to `window.location.reload()` when the poll reported
       the aircraft down; not needed — `useMyLocation` starts its watch the moment `enabled`
       flips true, and the landed layout below is its own branch, so it mounts fresh anyway. */

    /*
     * ⚠️ THE TWO WALKS OPEN OVER THIS SCREEN, THEY DO NOT REPLACE IT.
     *
     * Both are things you do WHILE the welcome screen's answer still stands — the driver,
     * the car and the booth are all still what you are walking towards — and a passenger
     * who swaps between the map and the written steps is choosing a method, not leaving.
     * As routes, each swap was a page load and the back button walked them out of the flow
     * one screen at a time.
     *
     * ⚠️ AND THEIR ROUTES ARE GONE. `/{tripId}/walk-to-pickup` and its `/steps` sibling
     * were removed with this change, so the sheet is the only way in and there is no second
     * URL that renders the same screens without the context around them.
     */
    const [walkView, setWalkView] = useState(null) // 'map' | 'steps' | 'guide' | null

    /**
     * THE WALK IS FINISHED — close the sheet AND the guide behind it.
     *
     * ⚠️ BOTH, BECAUSE THE WALK SCREENS NAVIGATE THEMSELVES. `navigate` below closes the
     * modal for the links THIS component owns, but the two walk views hold their own
     * `useNavigate` and go to /track-driver on their own — so the route changed under two
     * stacked modals and the passenger was left reading directions to a booth they were
     * already standing at, over a map they could not see.
     */
    const leaveWalk = () => {
        setWalkView(null)
        if (embedded) onLeave?.()
    }

    /*
     * The driver chat, opened by the message button on the driver card.
     *
     * ⚠️ THE STATE LIVES HERE, NOT IN THE CARD, so there is exactly one thread on the
     * screen — and now TWO controls open it: the driver card's message button and
     * "Experiencing a delay?".
     *
     * ⚠️ AND ONE THREAD IS THE POINT. A delay was briefly its own contact form, which
     * meant a passenger stuck at immigration typed into a box the driver's chat could not
     * see, then messaged the driver again to say the same thing. Telling your driver you
     * are late IS a message to your driver; there is nothing else it could usefully be.
     */
    const [chatOpen, setChatOpen] = useState(false)

    /* Telling the driver, which on this API means posting the booth's coordinate to the
       location stream — see `useAnnounceArrival` for why there is no arrival endpoint. */
    const {announce} = useAnnounceArrival(trip)

    /* ── WHICH SCREEN THIS IS, decided once and in this order ──────────────────────
       What follows below is written for someone standing in an arrivals hall: "Welcome!
       You've arrived", a driver who is waiting, a walk to a booth. Shown to a passenger
       still in the air it is not early, it is false. So: wait for the answer, then pick. */

    /**
     * ⚠️ WAIT BEFORE CHOOSING A LAYOUT — do not render one and then swap.
     *
     * The status decides which page this is, and on the first paint there is no status:
     * the trip is still loading. Choosing then means the ARRIVED layout every time, so a
     * passenger still in the air was shown "Welcome! You've arrived" and a walk to the
     * booth for a moment before it flipped to the tracking card. A flash of the wrong
     * answer is worse than a beat of nothing, because the wrong answer is one they might
     * act on — this screen's whole job is telling them whether to get up and walk.
     *
     * The skeleton is the one the route gate already uses, so the wait looks the same
     * whichever of the two is doing it.
     */
    if (tripLoading) return <ArrivalSkeleton/>

    /**
     * In the air — the tracking card, and nothing else.
     *
     * ⚠️ AND AN UNPOLLED FLIGHT KEEPS THE ARRIVED SCREEN. `flight` is null while
     * `provider_data` has not been filled in yet, and this page is reached BY the arrival
     * flow — a passenger is here because the backend said their flight landed. Treating
     * "no record yet" as "still flying" would withhold the walk from someone standing at
     * the gate, which is the worse of the two errors by a distance.
     */
    if (trackingFailed) {
        return <FlightOnTheWay flight={flight} flightNumber={trip?.flight_number} summary={trip?.flight} embedded={embedded} trackingFailed/>
    }

    if (airborne) {
        return <FlightOnTheWay flight={flight} flightNumber={trip?.flight_number} summary={trip?.flight} embedded={embedded}/>
    }

    return (
        <div className="flex flex-col gap-6 max-md:gap-4">
            {/* THE GREETING SPANS BOTH COLUMNS. It is addressed to the passenger, not to
                either half of the page, and centring it is what stops the two columns
                reading as two unrelated panels that happen to share a screen.

                ⚠️ DROPPED IN THE MODAL — its header already carries the same words, and
                two greetings stacked read as a rendering fault. */}
            {!embedded && <div className="text-center">
                <h2 className="mb-2! max-md:mb-1! max-md:text-2xl!">Welcome! You&apos;ve arrived.</h2>
                <p className="mx-auto mb-0! max-w-xl text-(--text-secondary) max-md:text-sm">
                    Your driver is waiting. Take your time, we&apos;ll walk you to the pickup point.
                </p>
            </div>}

            <div className="grid grid-cols-12 items-start gap-5 max-md:gap-3">
                {/* ── WHAT YOU DO NEXT ─────────────────────────────────────────────── */}
                <div className="col-span-12 flex flex-col gap-5 lg:col-span-7 max-md:gap-3">
                    {/* The two cards — next step, and the walk — shared with the
                        from-airport /track-driver page. See `ArrivalNextSteps`. */}
                    <ArrivalNextSteps
                        trip={trip}
                        tripId={tripId}
                        walkView={walkView}
                        onWalkViewChange={setWalkView}
                        onOpenChat={() => setChatOpen(true)}
                        onArrived={() => {
                            /* ⚠️ IT SENDS THE BOOTH'S COORDINATE, BECAUSE THERE IS NO
                               ARRIVAL ENDPOINT — see `useAnnounceArrival`. And it navigates
                               whether or not that lands: the call reports nothing back. */
                            announce()
                            navigate(`/${tripId}/track-driver`)
                        }}
                        onWalkDone={leaveWalk}
                    />
                </div>

                {/* ── WHO IS COMING FOR YOU ────────────────────────────────────────── */}
                <div className="col-span-12 flex flex-col gap-5 lg:col-span-5 max-md:gap-3">
                    {/* ⚠️ THE WHOLE TRIP, NOT JUST THE DRIVER. Calling runs through the
                        masking bridge, which is keyed on the trip and gated by its status —
                        a phone number on the driver would tell it nothing. */}
                    <PickupDriverCard
                        trip={trip}
                        chatOpen={chatOpen}
                        onOpenChat={() => setChatOpen(true)}
                    />

                    {/*
                        THE WAY OFF THIS SCREEN THAT IS NOT THE WALK.
                        ⚠️ IT BELONGS UNDER THE CAR, NOT UNDER THE PAGE. It ran full width
                        along the foot of both columns, which on a desktop meant a long grey
                        bar stretched under a column that had already ended — and it sat
                        directly beneath "View Step-by-Step Directions", reading as a third
                        row of the walk card rather than as a different question entirely.

                        Here it closes the column it actually belongs to: this side of the
                        screen answers "who is coming for me", and "what did I book" is the
                        next thing you would ask about the same ride.

                        ⚠️ QUIET, AND DELIBERATELY SO. Everything on the left answers "how do
                        I reach my car", which is the question a passenger has just landed
                        with. Grey rather than dark so it cannot compete with the walk button.

                        ⚠️ A PAGE, NOT A SHEET. It was briefly a modal holding the trip screen
                        embedded, on the reasoning that a detour should not leave the flow —
                        but the trip page is a full screen of its own, and a full screen
                        inside a dialog is a scrollbar inside a scrollbar. Back returns here,
                        which is what makes the detour cheap.
                    */}
                    <button
                        type="button"
                        onClick={() => navigate(`/rides/${tripId}`)}
                        className="flex min-h-14 w-full cursor-pointer items-center justify-center gap-3 rounded-full border-0 bg-(--bg-grey) px-6 text-lg font-bold text-(--text-primary) max-md:min-h-11 max-md:gap-2 max-md:px-4 max-md:text-sm"
                    >
                        View Trip Details
                        <img src={arrowRightSm} alt="" className="h-4 w-4"/>
                    </button>
                </div>
            </div>

            <TripChat
                tripId={trip?.id}
                driverName={trip?.driver?.name}
                driverPhoto={trip?.driver?.photo}
                open={chatOpen}
                onClose={() => setChatOpen(false)}
                /* ⚠️ THE CHAT CLOSES AS THE WALK OPENS. Two sheets at once would be a map
                   under a thread with two close buttons and an ambiguous Escape key — and a
                   passenger who has just pressed "start walking" is done reading. */
                onStartWalking={() => { setChatOpen(false); setWalkView('map') }}
            />

            {/*
                ⚠️ THE "LOCATION REQUIRED" BLOCK THAT STOOD HERE IS GONE (2026-09-22), and
                its reasoning is worth keeping because it was not wrong — it was answering
                the wrong question. Everything on this screen IS better with a position: the
                booth is chosen from one, the walk map needs one, and "I've arrived" is a
                location ping and nothing else. A driver whose passenger shares nothing does
                watch an empty map.

                But none of that is the PASSENGER's obligation. They are a customer standing
                in a terminal, and an undismissable modal over their own arrival screen —
                one they cannot answer if they tapped "block" once, weeks ago, on a different
                trip — takes the whole feature away to punish a setting. Location is now
                asked for and never demanded: `WelcomeModal` offers the prompt, the walk
                degrades to written directions, and the driver's map says "waiting for
                location" instead of pretending.
            */}
        </div>
    )
}

export {FromAirportArrived}
